/**
 * Shared Lao display labels + helpers for the Wallet Monitor admin pages, so every page renders the
 * same human-readable values (pay-type, wallet-type, pre-order, event, branch) and the same date format.
 */

// shipment_pay_type → Lao
export const PAY_TYPE_LABELS = {
  origin_freight_fees: 'ຈ່າຍຕົ້ນທາງ',
  origin_freight_fees_cod: 'ຈ່າຍຕົ້ນທາງ + COD',
  destination_freight_fees: 'ຈ່າຍປາຍທາງ',
  destination_freight_fees_cod: 'ຈ່າຍປາຍທາງ + COD',
};
export function payTypeLabel(t) {
  return t ? (PAY_TYPE_LABELS[t] || t) : '-';
}

// SWT event_type → Lao — keep in sync with Laravel's WalletMonitorLabels::EVENT (the export
// sheets must read identically to these screens). Insurance wording = DRAFT (native-speaker
// confirm before WALLET_INSURANCE_SPLIT_ENABLED flips).
export const EVENT_LABELS = {
  charge_create: 'ສ້າງ', charge_accept: 'ຮັບ', charge_success: 'ສຳເລັດ', charge_cod: 'COD',
  refund_issue: 'ຄືນ-ບັນຫາ', refund_customer_cancel: 'ຄືນ-ລູກຄ້າຍົກເລີກ', refund_dispose: 'ຄືນ-ທຳລາຍ',
  refund_cancel_success: 'ຄືນ-ຍົກເລີກສຳເລັດ', refund_compensate: 'ຄືນ-ຊົດເຊີຍ',
  adjust_increase: 'ປັບເພີ່ມ', adjust_decrease: 'ປັບຫຼຸດ',
  charge_insurance: 'ຄ່າປະກັນໄພ', refund_insurance_cancel: 'ຄືນ-ປະກັນໄພ ຍົກເລີກ',
  refund_insurance_dispose: 'ຄືນ-ປະກັນໄພ ທຳລາຍ', refund_insurance_issue: 'ຄືນ-ປະກັນໄພ ບັນຫາ',
};
export function eventLabel(e) {
  return e ? (EVENT_LABELS[e] || e) : '—';
}

/** wallet_type accepts an INT (1/2) or a STRING ('FREIGHT'/'COD'); COD shows as "COD", freight as "ຄ່າຂົນສົ່ງ". */
function isCod(wt) {
  return wt === 2 || wt === '2' || wt === 'COD';
}
export function walletLabel(wt) {
  return isCod(wt) ? 'COD' : 'ຄ່າຂົນສົ່ງ';
}
export function walletClass(wt) {
  return isCod(wt) ? 'cod' : 'freight';
}

/**
 * shipment_is_pre_order snapshot → Lao. The BFF emits this as a STRING ("1"/"0") or null (query-builder
 * stdClass + PDO TINYINT stringification), so coerce numerically — but guard null/'' FIRST, because
 * Number(null)===0 and Number('')===0 would otherwise mislabel a genuine Unknown as Direct.
 */
export function preOrderLabel(v) {
  if (v === null || v === undefined || v === '') return 'Unknown';
  if (Number(v) === 1 || v === true) return 'Pre-Order';
  if (Number(v) === 0 || v === false) return 'ສ້າງໂດຍກົງ';
  return 'Unknown';
}

/** shipment_success_type → Lao. */
export function successLabel(s) {
  const m = { normal: 'ສຳເລັດ', split: 'ແຍກ', returned: 'ຕີກັບ', returned_split: 'ຕີກັບແຍກ' };
  return s ? (m[s] || s) : '-';
}

/**
 * Date → "DD/MM/YYYY HH:mm" (24-hour). Handles the BFF's MySQL "YYYY-MM-DD HH:mm:ss", the Go API's
 * "DD-MM-YYYY HH:mm:ss", and ISO — without locale ambiguity.
 */
export function formatMonitorDateTime(s) {
  if (!s) return '-';
  const str = String(s);
  let m = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/); // MySQL / ISO
  if (m) { const [, y, mo, d, h, mi] = m; return `${d}/${mo}/${y} ${h}:${mi}`; }
  m = str.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})/); // Go DD-MM-YYYY
  if (m) { const [, d, mo, y, h, mi] = m; return `${d}/${mo}/${y} ${h}:${mi}`; }
  const dt = new Date(str);
  if (isNaN(dt.getTime())) return str;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

/** Today as "YYYY-MM-DD" — the default value for the monitor date inputs. */
export function todayStr() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Branch display "PREFIX - Name" from a branchId→branch map (built from the branch dropdown list) plus the
 * branch prefix already carried on the SWT row — so a raw branch id is never shown to the user.
 */
export function branchLabel(map, id, prefixFromRow, nameFromRow) {
  if (id === null || id === undefined || id === '') return '-';
  const b = map && map[id];
  const prefix = (b && b.prefix) || prefixFromRow || '';
  // name comes from the dropdown map (wallet-enabled branches only) OR the row itself — the API now
  // returns start/end_branch_name for every branch, so non-wallet branches no longer show code-only.
  const name = (b && b.name) || nameFromRow || '';
  if (prefix && name) return `${prefix} - ${name}`;
  if (prefix) return String(prefix);
  if (name) return name;
  return `#${id}`;
}

/**
 * Normalise the Go getWallets() result (which is an OBJECT { freight_wallet, cod_wallet }, possibly under
 * a data/ wallets envelope) into a uniform array of { wallet_type, balance, available } for display.
 */
export function normalizeLiveWallets(raw) {
  if (!raw) return [];
  let w = raw;
  if (!Array.isArray(w) && (w.data || w.wallets)) w = w.data || w.wallets;
  if (Array.isArray(w)) return w;
  if (w && (w.freight_wallet || w.cod_wallet)) {
    const out = [];
    if (w.freight_wallet) out.push({ wallet_type: 'FREIGHT', balance: w.freight_wallet.balance, available: w.freight_wallet.available });
    if (w.cod_wallet) out.push({ wallet_type: 'COD', balance: w.cod_wallet.balance, available: w.cod_wallet.available });
    return out;
  }
  return [];
}
